import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { ArrowLeft, Play, Square, Loader2, Volume2, Copy, Check } from 'lucide-react';
import { MyErotikaLogo } from '../components/MyErotikaLogo';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Available French voices
const AVAILABLE_VOICES = {
  women: [
    { id: 'fr-FR-VivienneMultilingualNeural', name: 'Vivienne', description: 'Naturelle, polyvalente' },
    { id: 'fr-FR-DeniseNeural', name: 'Denise', description: 'Chaleureuse, mature' },
    { id: 'fr-FR-EloiseNeural', name: 'Eloise', description: 'Douce, jeune' },
    { id: 'fr-CA-SylvieNeural', name: 'Sylvie (Québec)', description: 'Accent québécois' },
    { id: 'fr-BE-CharlineNeural', name: 'Charline (Belgique)', description: 'Accent belge' },
    { id: 'fr-CH-ArianeNeural', name: 'Ariane (Suisse)', description: 'Accent suisse' },
  ],
  men: [
    { id: 'fr-FR-RemyMultilingualNeural', name: 'Rémy', description: 'Naturel, polyvalent' },
    { id: 'fr-FR-HenriNeural', name: 'Henri', description: 'Élégant, mature' },
    { id: 'fr-CA-AntoineNeural', name: 'Antoine (Québec)', description: 'Accent québécois' },
    { id: 'fr-CA-JeanNeural', name: 'Jean (Québec)', description: 'Accent québécois, grave' },
    { id: 'fr-CA-ThierryNeural', name: 'Thierry (Québec)', description: 'Accent québécois' },
    { id: 'fr-BE-GerardNeural', name: 'Gérard (Belgique)', description: 'Accent belge' },
    { id: 'fr-CH-FabriceNeural', name: 'Fabrice (Suisse)', description: 'Accent suisse' },
  ]
};

// Characters
const CHARACTERS = {
  women: [
    { id: 'sofia', name: 'Sofia' },
    { id: 'mira', name: 'Mira' },
    { id: 'luna', name: 'Luna' },
    { id: 'lisa', name: 'Lisa' },
  ],
  men: [
    { id: 'alexandre', name: 'Alexandre' },
    { id: 'lucas', name: 'Lucas' },
    { id: 'victor', name: 'Victor' },
    { id: 'lorenzo', name: 'Lorenzo' },
  ],
  couples: [
    { id: 'sofia_alexandre', name: 'Sofia & Alexandre' },
    { id: 'mira_lucas', name: 'Mira & Lucas' },
    { id: 'elena_bastien', name: 'Elena & Bastien' },
    { id: 'luna_victor', name: 'Luna & Victor' },
  ]
};

export default function VoiceConfigPage() {
  const [selectedVoices, setSelectedVoices] = useState({
    sofia: 'fr-FR-VivienneMultilingualNeural',
    mira: 'fr-FR-VivienneMultilingualNeural',
    luna: 'fr-FR-VivienneMultilingualNeural',
    lisa: 'fr-FR-VivienneMultilingualNeural',
    alexandre: 'fr-FR-RemyMultilingualNeural',
    lucas: 'fr-FR-RemyMultilingualNeural',
    victor: 'fr-FR-RemyMultilingualNeural',
    lorenzo: 'fr-FR-RemyMultilingualNeural',
    sofia_alexandre: 'fr-FR-VivienneMultilingualNeural',
    mira_lucas: 'fr-FR-VivienneMultilingualNeural',
    elena_bastien: 'fr-FR-VivienneMultilingualNeural',
    luna_victor: 'fr-FR-VivienneMultilingualNeural',
  });
  
  const [playingVoice, setPlayingVoice] = useState(null);
  const [loadingVoice, setLoadingVoice] = useState(null);
  const [copied, setCopied] = useState(false);
  const audioRef = useRef(null);

  const testPhrases = {
    sofia: "Bonjour, je suis Sofia. J'ai hâte de faire ta connaissance...",
    mira: "Salut, c'est Mira. Viens, approche-toi...",
    luna: "Bonsoir, Luna à ton service. La nuit nous appartient...",
    lisa: "Hey ! Moi c'est Lisa. Prêt pour l'aventure ?",
    alexandre: "Bonsoir, je suis Alexandre. Enchanté de te rencontrer...",
    lucas: "Salut, moi c'est Lucas. On va bien s'amuser ensemble...",
    victor: "Bonsoir. Victor. Je sens qu'on va passer un bon moment...",
    lorenzo: "Buonasera... Je suis Lorenzo. Laisse-moi te séduire...",
    sofia_alexandre: "Bonsoir, nous sommes Sofia et Alexandre...",
    mira_lucas: "Salut ! Mira et Lucas, à votre service...",
    elena_bastien: "Bonsoir, Elena et Bastien ici...",
    luna_victor: "Bonsoir, Luna et Victor vous accueillent...",
  };

  const playVoice = async (characterId, voiceId) => {
    // Stop current audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    
    if (playingVoice === `${characterId}-${voiceId}`) {
      setPlayingVoice(null);
      return;
    }

    setLoadingVoice(`${characterId}-${voiceId}`);
    
    try {
      const response = await axios.post(`${API}/tts/test`, {
        text: testPhrases[characterId],
        voice_id: voiceId
      });
      
      if (response.data.audio_url) {
        const audio = new Audio(response.data.audio_url);
        audioRef.current = audio;
        
        audio.onended = () => {
          setPlayingVoice(null);
          audioRef.current = null;
        };
        
        await audio.play();
        setPlayingVoice(`${characterId}-${voiceId}`);
      }
    } catch (error) {
      toast.error('Erreur lors de la lecture');
    } finally {
      setLoadingVoice(null);
    }
  };

  const handleVoiceChange = (characterId, voiceId) => {
    setSelectedVoices(prev => ({
      ...prev,
      [characterId]: voiceId
    }));
  };

  const generateConfig = () => {
    const config = `CHARACTER_VOICES = {
    # Femmes
    "sofia": "${selectedVoices.sofia}",
    "mira": "${selectedVoices.mira}",
    "luna": "${selectedVoices.luna}",
    "lisa": "${selectedVoices.lisa}",
    # Hommes
    "alexandre": "${selectedVoices.alexandre}",
    "lucas": "${selectedVoices.lucas}",
    "victor": "${selectedVoices.victor}",
    "lorenzo": "${selectedVoices.lorenzo}",
    # Couples
    "sofia_alexandre": "${selectedVoices.sofia_alexandre}",
    "mira_lucas": "${selectedVoices.mira_lucas}",
    "elena_bastien": "${selectedVoices.elena_bastien}",
    "luna_victor": "${selectedVoices.luna_victor}"
}`;
    return config;
  };

  const copyConfig = () => {
    navigator.clipboard.writeText(generateConfig());
    setCopied(true);
    toast.success('Configuration copiée !');
    setTimeout(() => setCopied(false), 2000);
  };

  const renderCharacterSection = (title, characters, voiceOptions) => (
    <div className="mb-8">
      <h3 className="text-xl font-heading text-purple-light mb-4">{title}</h3>
      <div className="space-y-4">
        {characters.map(character => (
          <div key={character.id} className="glass rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-velvet_text-primary font-medium">{character.name}</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {voiceOptions.map(voice => (
                <button
                  key={voice.id}
                  onClick={() => handleVoiceChange(character.id, voice.id)}
                  className={`flex items-center gap-2 p-3 rounded-lg transition-all ${
                    selectedVoices[character.id] === voice.id
                      ? 'bg-purple-main text-white'
                      : 'bg-white/5 hover:bg-white/10 text-velvet_text-secondary'
                  }`}
                >
                  <div className="flex-1 text-left">
                    <div className="text-sm font-medium">{voice.name}</div>
                    <div className="text-xs opacity-70">{voice.description}</div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      playVoice(character.id, voice.id);
                    }}
                    className="p-2 rounded-full bg-white/10 hover:bg-white/20"
                  >
                    {loadingVoice === `${character.id}-${voice.id}` ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : playingVoice === `${character.id}-${voice.id}` ? (
                      <Square size={14} />
                    ) : (
                      <Play size={14} />
                    )}
                  </button>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-velvet-bg">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-velvet_text-secondary hover:text-velvet_text-accent">
            <ArrowLeft size={20} />
            <MyErotikaLogo size="sm" />
          </Link>
          <h1 className="text-velvet_text-primary font-medium">Configuration des voix</h1>
        </div>
      </header>

      {/* Content */}
      <main className="pt-24 pb-32 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <Volume2 className="mx-auto text-purple-light mb-4" size={48} />
            <h2 className="font-heading text-3xl gradient-text mb-2">Paramétrage des voix</h2>
            <p className="text-velvet_text-secondary">
              Sélectionnez une voix pour chaque personnage et cliquez sur ▶ pour l'écouter
            </p>
          </div>

          {renderCharacterSection('Femmes', CHARACTERS.women, AVAILABLE_VOICES.women)}
          {renderCharacterSection('Hommes', CHARACTERS.men, AVAILABLE_VOICES.men)}
          {renderCharacterSection('Couples (voix principale)', CHARACTERS.couples, AVAILABLE_VOICES.women)}

          {/* Config Output */}
          <div className="glass rounded-xl p-6 mt-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-heading text-velvet_text-primary">Configuration à envoyer</h3>
              <Button
                onClick={copyConfig}
                className="bg-purple-main hover:bg-purple-dark text-white"
              >
                {copied ? <Check size={16} className="mr-2" /> : <Copy size={16} className="mr-2" />}
                {copied ? 'Copié !' : 'Copier'}
              </Button>
            </div>
            <pre className="bg-black/50 rounded-lg p-4 text-sm text-green-400 overflow-x-auto">
              {generateConfig()}
            </pre>
          </div>
        </div>
      </main>
    </div>
  );
}
